package com.evoapps.evoshift.ui.user

import com.evoapps.evoshift.widgets.MenuRowCard
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Language
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.RadioButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.cardClickable
import com.evoapps.evoshift.viewmodels.LanguageViewModel
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.EvoDialog
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.language_change
import evoshift.composeapp.generated.resources.language_device
import evoshift.composeapp.generated.resources.language_device_current
import evoshift.composeapp.generated.resources.language_hint
import evoshift.composeapp.generated.resources.language_name_en
import evoshift.composeapp.generated.resources.language_name_pl
import evoshift.composeapp.generated.resources.language_name_uk
import evoshift.composeapp.generated.resources.language_title
import org.jetbrains.compose.resources.stringResource

/** Test tags of the language picker (SH-137). */
object LanguageTags {
    const val CARD = "language_card"
    const val BUTTON = "language_button"
    fun option(language: String?) = "language_option_${language ?: "device"}"
}

/** A language's own name ("Polski", "English", "Українська"), the same in every language. */
@Composable
fun languageName(language: String): String = when (language) {
    "pl" -> stringResource(Res.string.language_name_pl)
    "uk" -> stringResource(Res.string.language_name_uk)
    else -> stringResource(Res.string.language_name_en)
}

/** "Device language (English)" for no pick, else the picked language's name. */
@Composable
private fun pickedLabel(state: LanguageViewModel.UiState): String =
    state.picked?.let { languageName(it) } ?: stringResource(Res.string.language_device_current, languageName(state.shown))

/** Profile: "Language" with the current choice; opens [LanguageDialog]. */
@Composable
fun LanguageCard(viewModel: LanguageViewModel) {
    val state by viewModel.state.collectAsState()
    // Saveable: picking switches the language, which recomposes the whole app.
    var open by rememberSaveable { mutableStateOf(false) }
    MenuRowCard(Icons.Default.Language, stringResource(Res.string.language_title), onClick = { open = true }, modifier = Modifier.testTag(LanguageTags.CARD), subtitle = pickedLabel(state))
    if (open) LanguageDialog(viewModel, onDismiss = { open = false })
}

/** The sign-in screen: a small "Language" button, for picking before having an account. */
@Composable
fun LanguageButton(viewModel: LanguageViewModel, modifier: Modifier = Modifier) {
    val state by viewModel.state.collectAsState()
    var open by rememberSaveable { mutableStateOf(false) }
    val label = pickedLabel(state)
    TextButton(onClick = { open = true }, modifier = modifier.testTag(LanguageTags.BUTTON)) {
        Icon(Icons.Default.Language, contentDescription = stringResource(Res.string.language_change, label), tint = LightGold)
        Spacer(Modifier.width(8.dp))
        Text(label, color = LightGold)
    }
    if (open) LanguageDialog(viewModel, onDismiss = { open = false })
}

/** The device's language, Polski, English, Українська; picking one closes the dialog. */
@Composable
fun LanguageDialog(viewModel: LanguageViewModel, onDismiss: () -> Unit) {
    val state by viewModel.state.collectAsState()
    EvoDialog(
        title = stringResource(Res.string.language_title),
        icon = Icons.Default.Language,
        onDismiss = onDismiss,
        width = DialogWidth.Compact
    ) {
        Text(stringResource(Res.string.language_hint), color = Color.White.copy(alpha = 0.85f))
        Column(Modifier.selectableGroup(), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            viewModel.options.forEach { option ->
                val selected = option == state.picked
                val label = option?.let { languageName(it) } ?: stringResource(Res.string.language_device)
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .testTag(LanguageTags.option(option))
                        .selectable(selected = selected, role = Role.RadioButton) {
                            onDismiss()
                            viewModel.choose(option)
                        }
                        .padding(vertical = 8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    RadioButton(
                        selected = selected,
                        onClick = null,
                        colors = RadioButtonDefaults.colors(selectedColor = LightGold, unselectedColor = Color.White.copy(alpha = 0.7f))
                    )
                    Spacer(Modifier.width(12.dp))
                    Text(label, color = Color.White)
                }
            }
        }
    }
}
